import { SIMPLMenuHeader } from "../../components/comons/header/SIMPLMenuHeader";
import { SIMPLTable } from "../../components/comons/table/SimplTable";
import { Layout } from "../../components/comons/layout/Layout";
import { LABELS } from "~/resources/Labels";
import { SIMPLTitle } from "../../components/comons/title/SIMPLTitle";
import { ChangeEvent, useEffect, useState } from "react";
import { PropRow, SortTable } from "~/models/Comons";
import { ContractHeader } from "~/models/scripts/ContractHeader";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Entries } from "~/models/enums/Entries";
import {
  attachResourcesToRequesters,
  displayEntries,
  sortScripts,
} from "~/Utils/Utils";
import {
  useDeleteContractsMutation,
  useGetContractsQuery,
} from "~/services/contacts";
import { SIMPLButtonCustomized } from "../../components/comons/button/SIMPLButtonCustumized";
import { SIMPLAlert } from "../../components/comons/dialog/SimplAlert";
import { MESSAGES } from "~/resources/Message";
import { toast } from "react-toastify";
import { RequesterInfo } from "~/models/types/ContractsTypes";
import { PayloadType } from "~/models/types/Payload";
import { buildLogoutUrl } from "~/services/keycloak";
import useSessionTimeout from "~/services/session/SessionTimeOut";

export const SIMPLContract = () => {
  const [deleteContracts] = useDeleteContractsMutation();
  const [active] = useState<boolean>(true);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [item, setItem] = useState({} as RequesterInfo);
  const [isAddModalOpenDecommission, setIsAddModalOpenDecommission] =
    useState(false);
  const [rows, setRows] = useState([] as PropRow[]);
  const [size, setSize] = useState<number>(5);
  const [page, setPage] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [showPagination, setShowPagination] = useState(true);
  const [totalElements, setTotalElements] = useState<number>(0);
  const INACTIVITY_LIMIT = Number(import.meta.env.VITE_INACTIVITY_LIMIT); 
  const FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN = Number(
    import.meta.env.VITE_FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN
  );
  const { data, refetch } = useGetContractsQuery({ page, size, active });
  const [sortConfig, setSortConfig] = useState<SortTable>({
    key: "",
    direction: LABELS.ASC,
  });
  const handleSort = (key: string) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === LABELS.ASC
        ? LABELS.Desc
        : LABELS.ASC;
    setSortConfig({ key, direction });
  };

  /**
   * Initializes session timeout handling.
   *
   * @param {Object} config - Configuration for session timeout.
   * @param {number} config.inactivityLimit - Time in ms before user is considered inactive.
   * @param {number} config.forcedLogoutLimit - Time in ms before forced logout after inactivity.
   * @param {Function} config.buildLogoutUrl - Function that returns the logout URL.
   * @param {Storage} config.storage - Storage mechanism (e.g., sessionStorage).
   * */
  useSessionTimeout({
    inactivityLimit: INACTIVITY_LIMIT,
    forcedLogoutLimit: FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN,
    buildLogoutUrl,
    storage: sessionStorage,
  });

  /**
   * Initializes and sets the table rows by:
   * 1. Filtering scripts based on the active tab.
   * 2. Sorting the filtered scripts according to the sort configuration.
   * 3. Mapping sorted scripts to table row format and setting rows.
   */
  const initContracts = () => {
    // Ensure payload exists before processing
    const payload = data?.payload as PayloadType | undefined;

    if (!payload || payload.length === 0) {
      setShowPagination(false);
      return;
    }
    // Attach resources, sort scripts, and customize entries
    const contractsMergedList = attachResourcesToRequesters(payload);
    const sortedScripts = sortScripts(contractsMergedList, sortConfig);
    const customizedList = displayEntries(
      sortedScripts,
      totalElements,
      Entries.Twenty
    );
    // Set pagination visibility and total elements count
    const totalPages = payload?.page?.totalPages ?? 0;
    setTotal(Number(totalPages));

    if (payload?.page?.totalElements) {
      setTotalElements(payload?.page?.totalElements);
    }

    // Process rows and set them
    const scriptRows: PropRow[] = customizedList.map((contractItem: any) => ({
      row: contractItem,
    }));

    setRows(scriptRows);
    setShowPagination(true);
  };

  useEffect(() => {
    initContracts();
  }, [data, active, sortConfig, size, total, totalElements]);

  /**
   * @effect
   * - Runs when the 'active' dependency changes.
   * - Calls setPage(1) to reset the pagination.
   */
  useEffect(() => {
    setPage(0);
    handlePaginationSize();
  }, [active, size]);

  const handlePaginationSize = () => {
    if ([Entries.Ten, Entries.Twenty].includes(size)) {
      setPage(0);
    }
  };
  /* Renders "Close" and "Start" buttons for alerts */
  const renderDecommissionButtons = () => (
    <>
      <div className="grow-0 basis-32 mr-5">
        <SIMPLButtonCustomized
          label="Close"
          secondary
          buttonHandlerAction={() => setIsAddModalOpenDecommission(false)}
        />
      </div>
      <div className="grow-0 basis-32">
        <SIMPLButtonCustomized
          label="Start"
          secondIcon={<PlayArrowIcon fontSize="small" />}
          primary
          buttonHandlerAction={handleDecommision}
        />
      </div>
    </>
  );

  const handleConfirmDecommission = (contractItem: RequesterInfo) => {
    if (contractItem.requesterUniqueId) {
      setItem(contractItem);
      setIsAddModalOpenDecommission(true);
    } else {
      console.warn(LABELS.ScriptIdDonotExist);
    }
  };
  const handleDecommision = async () => {
    if (!item?.requesterUniqueId) {
      toast.error(MESSAGES.ErrorDecommissionIdMissing);
      return;
    }

    try {
      await toast.promise(
        async () => {
          const { success } = await deleteContracts(
            item.requesterUniqueId
          ).unwrap();
          return success;
        },
        {
          pending: "Decommission Pending...",
          success: MESSAGES.MessagemDecommission,
          error: MESSAGES.ErrorDecommission,
        }
      );
    } catch (error) {
      console.error(MESSAGES.ErrorDecommission, error);
    } finally {
      setIsAddModalOpenDecommission(false);
      refetch();
    }
  };

  /*Renders decommission alert with close and start actions */
  const renderDecommisionAlert = () =>
    isAddModalOpenDecommission && (
      <SIMPLAlert
        onCancel={() => setIsAddModalOpenDecommission(false)}
        title={LABELS.TitleDecommission}
        actions={renderDecommissionButtons()}
        message={""}
      >
        <p>{MESSAGES.MessageDecommission}</p>
      </SIMPLAlert>
    );
  const showFilterHandler = () => setShowFilter((prev) => !prev);

  const handleSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSize(Number(e.target.value));
  };
  const handleResetAll = () => {
    setPage(0);
    setShowFilter(false);
  };
  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const renderSIMPLTable = () => (
    <div className="flex flex-col w-full">
      <div className="mt-5">
        <div className="container mx-auto mt-10">
          <SIMPLTable
            clear={handleResetAll}
            page={page}
            total={total}
            totalElements={totalElements}
            showPagination={showPagination}
            active={true}
            showHandler={showFilterHandler}
            filterActive={showFilter}
            tableHeaders={ContractHeader}
            handleDecommision={handleConfirmDecommission}
            rows={rows}
            onChangePage={handlePageChange}
            onChange={handleSizeChange}
            sortConfig={sortConfig}
            handleSort={handleSort}
            chipLabels={[]}
            showButtonDecommission
          />
        </div>
      </div>
    </div>
  );
  const renderSIMPLTitle = () => (
    <SIMPLTitle title={LABELS.DecommissionManagment} />
  );
  return (
    <SIMPLMenuHeader>
      <Layout>
        {renderSIMPLTitle()}
        {renderSIMPLTable()}
        {renderDecommisionAlert()}
      </Layout>
    </SIMPLMenuHeader>
  );
};
